import React, { useState, useEffect } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { useClientMessages } from './Client/ClientMessageContext';
import { useGeneralMessages } from './General/GeneralMessageContext';

interface MessageFullScreenProps {
  className?: string;
}

export const MessageFullScreen: React.FC<MessageFullScreenProps> = ({ className = '' }) => {
  const { visibleMessages: clientMessages, dismissMessage: dismissClientMessage } = useClientMessages();
  const { visibleMessages: generalMessages, dismissMessage: dismissGeneralMessage } = useGeneralMessages();
  const [openScreens, setOpenScreens] = useState<Set<string>>(new Set());

  // Filter for full screen type messages only
  const clientFullScreenMessages = clientMessages.filter(message => 
    message?.data?.displayType === 'FULL_SCREEN'
  );
  
  const generalFullScreenMessages = generalMessages.filter(message => 
    message?.data?.displayType === 'FULL_SCREEN'
  );

  // Combine both types of messages
  const allFullScreenMessages = [...clientFullScreenMessages, ...generalFullScreenMessages];

  // Auto-open full screen messages when they become available
  useEffect(() => {
    allFullScreenMessages.forEach(message => {
      if (!openScreens.has(message.id)) {
        // Small delay to ensure smooth animation
        const timer = setTimeout(() => {
          setOpenScreens(prev => new Set(prev).add(message.id));
        }, 100);
        
        return () => clearTimeout(timer);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openScreens]);

  if (allFullScreenMessages.length === 0) {
    return null;
  }

  const handleButtonClick = (message: any) => {
    if (!message?.data?.button) return;
    
    const { action, link } = message.data.button;
    
    switch (action) {
      case 'INTERNAL_LINK':
        if (link) {
          window.location.href = link;
        }
        break;
      case 'EXTERNAL_LINK':
        if (link) {
          window.open(link, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'CLOSE':
        // Determine which dismiss function to use based on message source
        if (clientFullScreenMessages.some(m => m.id === message.id)) {
          dismissClientMessage(message.id);
        } else {
          dismissGeneralMessage(message.id);
        }
        break;
      case 'NONE':
      default:
        // Do nothing
        break;
    }
  };

  const handleBackdropClick = (e: React.MouseEvent, messageId: string, isDismissible: boolean) => {
    if (e.target === e.currentTarget && isDismissible) {
      // Determine which dismiss function to use based on message source
      if (clientFullScreenMessages.some(m => m.id === messageId)) {
        dismissClientMessage(messageId);
      } else {
        dismissGeneralMessage(messageId);
      }
    }
  };

  return (
    <RemoveScroll enabled={openScreens.size > 0}>
      {allFullScreenMessages.map((message) => {
        const isOpen = openScreens.has(message.id);
        const button = message?.data?.button || {};
        const image = message?.data?.image || {};
        const isDismissible = message.data?.dismissible || false;
        
        return (
          <div key={message.id}>
            {/* Full Screen Overlay */}
            {isOpen && (
              <div 
                className="message-fullscreen-overlay"
                onClick={(e) => handleBackdropClick(e, message.id, isDismissible)}
              >
                <div className="message-fullscreen">
                  <div className="message-fullscreen-header">
                    <h1 className="message-fullscreen-title">{message.data?.title || 'Important Notice'}</h1>
                    {isDismissible && (
                      <button
                        className="message-fullscreen-close"
                        onClick={() => {
                          // Determine which dismiss function to use based on message source
                          if (clientFullScreenMessages.some(m => m.id === message.id)) {
                            dismissClientMessage(message.id);
                          } else {
                            dismissGeneralMessage(message.id);
                          }
                        }}
                        aria-label="Close full screen message"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  
                  <div className="message-fullscreen-content">
                    {/* Image Section */}
                    {image.enabled && image.url && (
                      <div className="message-fullscreen-image">
                        <img 
                          src={image.url} 
                          alt={message.data?.title || 'Message image'}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Message Text */}
                    <div className="message-fullscreen-text">
                      <p>{message.data?.message || ''}</p>
                    </div>
                  </div>
                  
                  {/* Full Screen Actions */}
                  {button.enabled && button.text && (
                    <button
                      className="message-fullscreen-button"
                      onClick={() => handleButtonClick(message)}
                    >
                      {button.text}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </RemoveScroll>
  );
}; 